/**
 * The Signals walkthrough — four screens, once.
 *
 * Signals is the one panel that cannot be understood by poking at it, because everything it does
 * happens somewhere else, later, to somebody else. Without this, the first thing a person does
 * with the box is type something careful into it to see what happens, and careful is exactly what
 * the box exists to avoid.
 *
 * Skip and "Got it" do the same thing, on purpose: both are the person saying they are done being
 * told about this. The flag is stamped server-side, so it follows them to a new phone and a
 * cleared cache does not start the explaining again.
 *
 * It is an overlay and not a `Panel`, so it draws its own header — and draws it the way every
 * panel's is drawn: the look's own cut of Fraunces, and the same ⓘ opening the same Signals guide.
 * A page that is manifestly a Signals page in an upright face with no ⓘ is a page from a different
 * app.
 */
import { PanelGuide } from '@/components/panel';
import { StepPreview } from '@/components/step-preview';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { useStore } from '@/store';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

const SAMPLE = 'I wish we went hiking more. It’s been months and I miss it.';

/** The three example steps in stage 3 — one of each shape a plan can take. */
const EXAMPLES = [
  { panel: 'sync_probe', phrasing: 'Free Saturday: outdoors or indoors?', payload: { type: 'compare', text: 'Free Saturday: outdoors or indoors?' } },
  { panel: 'date_card', phrasing: 'A trail and a picnic.', payload: { title: 'Trail and picnic' } },
  { panel: 'mission', phrasing: 'Plan one thing this weekend that isn’t at home.', payload: { title: 'Plan one thing this weekend that isn’t at home.' } },
];

export function SignalsWalkthrough() {
  const { me, openOverlay, markWalkthroughSeen, setSignalDraft, go } = useStore();
  const look = useStore((s) => s.look);
  const [stage, setStage] = React.useState(1);
  const [guide, setGuide] = React.useState(false);
  const [sample, setSample] = React.useState(SAMPLE);
  const them = me?.partnerName ?? 'your partner';

  const finish = async (carryDraft: boolean) => {
    // Only an EDITED sample is carried across. The tutorial's own sentence sitting in the live box
    // is one press from being filed as a real signal about somebody's hiking.
    if (carryDraft && sample.trim() && sample.trim() !== SAMPLE) setSignalDraft({ kind: 'desire', body: sample.trim() });
    openOverlay(null);
    go('signals');
    await markWalkthroughSeen();
  };

  return (
    <View className="flex-1 gap-3">
      <View className="flex-row items-center justify-between gap-2">
        <View className="min-w-0 flex-row items-center gap-2">
          {/* `fit-opsz` is web-only, and accepted as such: a static font instance has no `opsz`
              axis for a phone to move, so on native the class is inert and the title draws at
              Fraunces' default optical size. Same branch as `panel.tsx`. */}
          <Text variant="title" className={look === 'bouquet' ? 'italic' : 'fit-opsz'}>
            Signals
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="What is Signals for?"
            onPress={() => setGuide((v) => !v)}
            className="border-line-strong size-6 items-center justify-center rounded-full border active:opacity-70">
            <Text variant="small" className="text-muted-foreground">
              ⓘ
            </Text>
          </Pressable>
        </View>
        <Button variant="ghost" size="sm" flush={false} onPress={() => void finish(false)}>
          <Text>Skip</Text>
        </Button>
      </View>
      <Text variant="small" className="text-muted-foreground">
        How this works — once
      </Text>

      {guide ? <PanelGuide id="signals" onClose={() => setGuide(false)} /> : null}

      <ScrollView className="flex-1" contentContainerClassName="gap-3 pb-6" keyboardShouldPersistTaps="handled">
        {stage === 1 ? (
          <>
            <Card>
              <CardContent>
                <CardHead>How Signals works</CardHead>
                <Text>
                  This is the one place to say the honest thing. What you type is{' '}
                  <Text className="font-semibold">never shown</Text> to {them}. Not softened, not paraphrased — never
                  shown.
                </Text>
                <Text>
                  The app drafts a plan for how to bring it about indirectly. You approve the plan. Then the app picks
                  its moments — and even you will not know when.
                </Text>
              </CardContent>
            </Card>
            <Button className="self-start" onPress={() => setStage(2)}>
              <Text>Try one</Text>
            </Button>
          </>
        ) : null}

        {stage === 2 ? (
          <>
            <Card>
              <CardContent>
                <CardHead>1 · Say it plainly</CardHead>
                <CardDescription>A sample. Change it to something of your own if you like.</CardDescription>
                <Textarea value={sample} onChangeText={setSample} maxLength={2000} />
              </CardContent>
            </Card>
            <Button className="self-start" onPress={() => setStage(3)}>
              <Text>See the plan it makes</Text>
            </Button>
          </>
        ) : null}

        {stage === 3 ? (
          <>
            <Card>
              <CardContent>
                <CardHead>2 · The plan</CardHead>
                <CardDescription>Spread over panels, over time. No step says who asked.</CardDescription>
              </CardContent>
            </Card>
            {/* The same `StepPreview` the review screen uses, and the same one stage 4 uses — the
                three steps drawn as the cards they become rather than as a bare label over a
                sentence, which was a fourth way of labelling a step in an app that has one. */}
            {EXAMPLES.map((example) => (
              <StepPreview key={example.panel} step={example} partnerName={them} />
            ))}
            <Button className="self-start" onPress={() => setStage(4)}>
              <Text>See what {them} sees</Text>
            </Button>
          </>
        ) : null}

        {stage === 4 ? (
          <>
            {/* A heading with nothing under it, so the band IS the card: `pb-1` leaves the lip the
                band's own 4px bottom margin needs and no more. `CardContent`'s usual 16px would
                read as a card whose body failed to arrive. */}
            <Card>
              <CardContent className="pb-1">
                <CardHead>{`3 · Exactly as ${them} will see it`}</CardHead>
              </CardContent>
            </Card>
            <StepPreview
              step={{ panel: 'mission', phrasing: EXAMPLES[2]!.phrasing, payload: EXAMPLES[2]!.payload }}
              partnerName={them}
            />
            <Text variant="small" className="text-muted-foreground">
              Some missions come from the game; some were planted. {them} cannot tell which — and neither can you, once
              it is out.
            </Text>
            <Button className="self-start" onPress={() => void finish(true)}>
              <Text>Got it — open Signals</Text>
            </Button>
          </>
        ) : null}
      </ScrollView>
    </View>
  );
}
